import { Injectable } from '@angular/core';
import { ApiService } from '@shared/services/api.service';
import {
  BehaviorSubject,
  Observable,
  finalize,
  map,
  of,
  shareReplay,
  tap,
} from 'rxjs';
import { AccessResponse } from './access-response.model';
import { Permission } from './permissions';

@Injectable({
  providedIn: 'root',
})
export class AccessService {
  private readonly accessSubject =
    new BehaviorSubject<AccessResponse | null>(null);

  private capabilitySet = new Set<Permission>();

  private loadRequest$: Observable<AccessResponse> | null = null;

  readonly access$ = this.accessSubject.asObservable();

  readonly previewMode$ = this.access$.pipe(
    map((access) => access?.preview ?? false),
  );

  readonly loaded$ = this.access$.pipe(
    map((access) => access !== null),
  );

  constructor(private readonly api: ApiService) { }

  get current(): AccessResponse | null {
    return this.accessSubject.value;
  }

  get isLoaded(): boolean {
    return this.current !== null;
  }

  get isPreviewMode(): boolean {
    return this.current?.preview ?? false;
  }

  get canWrite(): boolean {
    return !this.isPreviewMode;
  }

  ensureLoaded(forceReload = false): Observable<AccessResponse> {
    if (this.loadRequest$ !== null) {
      return this.loadRequest$;
    }

    if (!forceReload && this.current !== null) {
      return of(this.current);
    }

    const request$ = this.api.getMe().pipe(
      tap((access) => {
        this.updateAccessAsync(access);
      }),
      finalize(() => {
        this.loadRequest$ = null;
      }),
      shareReplay({
        bufferSize: 1,
        refCount: false,
      }),
    );

    this.loadRequest$ = request$;

    return request$;
  }

  reload(): Observable<AccessResponse> {
    return this.api.getMe().pipe(
      tap((access) => {
        this.updateAccessAsync(access);
      }),
    );
  }

  clear(): void {
    this.capabilitySet = new Set<Permission>();
    this.accessSubject.next(null);
    this.loadRequest$ = null;
  }

  hasPermission(permission: Permission): boolean {
    return this.capabilitySet.has(permission);
  }

  hasAny(permissions: readonly Permission[]): boolean {
    return permissions.some((permission) =>
      this.hasPermission(permission),
    );
  }

  hasAll(permissions: readonly Permission[]): boolean {
    return permissions.every((permission) =>
      this.hasPermission(permission),
    );
  }

  private updateAccessAsync(access: AccessResponse): void {
    queueMicrotask(() => {
      this.setAccess(access);
    });
  }

  private setAccess(access: AccessResponse): void {
    this.capabilitySet = new Set(access.capabilities);
    this.accessSubject.next(access);
  }
}